import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

interface EmptyStateProps {
  icon?: React.ReactNode;
  /** A simple line illustration (SOUL-04). Shown instead of the icon when given. */
  illustration?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode | {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon,
  illustration,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  const actionContent = action && typeof action === "object" && "label" in action
    ? <Button size="sm" onClick={action.onClick}>{action.label}</Button>
    : action;

  return (
    <div
      className={cn("w-full", className)}
    >
      <Empty className="min-h-64 gap-5 rounded-none border-0 px-6 py-10 md:p-10">
        <EmptyHeader className="gap-2.5">
          {illustration ? (
            <div className="mb-2 text-brand" aria-hidden="true">{illustration}</div>
          ) : icon && (
            <EmptyMedia
              variant="icon"
              className="mb-1 size-11 border border-primary/15 bg-primary/[0.045] text-primary"
            >
              {icon}
            </EmptyMedia>
          )}
          <EmptyTitle className="text-base font-semibold text-foreground">
            {title}
          </EmptyTitle>
          {description && (
            <EmptyDescription className="max-w-md text-[15px] leading-6">
              {description}
            </EmptyDescription>
          )}
        </EmptyHeader>
        {actionContent && (
          <EmptyContent className="gap-2">
            {actionContent}
          </EmptyContent>
        )}
      </Empty>
    </div>
  );
}
